// ── AR Flow — Entregas a partir do LIFT: regras (sem tela) ──────────────────
// O LIFT manda três tipos de informação com CERTEZAS diferentes, e a tela
// nunca mistura os três:
//   • STATUS do pedido no LIFT ...................... certeza (o que o LIFT diz)
//   • PREVISÃO de entrega por item ................... previsão do fornecedor
//   • "Chegou no depósito" / "Entrega percebida em" .. dedução do CRM pela
//     mudança de status (não é confirmação física nem hora exata)
//   • entrega.entregaFonte === "deposito" ............. confirmação manual da loja
// Processo da loja (Ana): encomenda vai para a fábrica; quando chega ao
// depósito, o gerente do depósito liga e agenda com o cliente (fora do LIFT);
// o LIFT só marca ENTREGUE depois da entrega. Pronta entrega sai em até 1 semana.

const DIA = 24 * 60 * 60 * 1000;
export const NA_FABRICA = new Set(["OC PENDENTE", "OC ENVIADA", "ATRASADO", "PENDENTE"]);
export const OCULTOS = new Set(["CANCELADO", "CANCELADA"]);

export const COLUNAS = [
  { id: "fabrica", titulo: "Na fábrica", desc: "Encomenda com o fornecedor", cor: "#B45309" },
  { id: "deposito", titulo: "No depósito · agendar", desc: "Chegou; o depósito liga e agenda", cor: "#1D4ED8" },
  { id: "pronta", titulo: "Pronta entrega", desc: "Sai em até 1 semana", cor: "#6D28D9" },
  { id: "entregue", titulo: "Entregue", desc: "Neste mês", cor: "#1E6B4A" },
  { id: "outros", titulo: "Outros status", desc: "Status do LIFT sem regra definida", cor: "#64748B" },
];

const dia = v => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
// Timestamps do CRM vêm em UTC; o mês operacional é o de São Paulo.
function diaLocal(v) {
  const data = dia(v);
  if (!data || !/T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(v)) return data;
  const instante = new Date(v);
  if (!Number.isFinite(instante.getTime())) return null;
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(instante);
  const valor = tipo => partes.find(p => p.type === tipo)?.value;
  return `${valor("year")}-${valor("month")}-${valor("day")}`;
}
function diasEntre(deYmd, ateYmd) {
  const a = Date.parse(`${deYmd}T12:00:00Z`), b = Date.parse(`${ateYmd}T12:00:00Z`);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.round((b - a) / DIA) : null;
}

export function classificarPedido(p, hoje) {
  const status = String(p?.statusLift || "").trim().toUpperCase();
  if (!p?.liftPedidoId || OCULTOS.has(status)) return null;
  const entrega = p.entrega || {};
  const tipo = entrega.tipo === "pronta_entrega" ? "pronta_entrega" : "encomenda";
  const previsao = dia(entrega.previsaoUltimoItem);
  const chegou = dia(entrega.chegouNoDepositoEm);
  const entregaManualEm = entrega.entregaFonte === "deposito" ? dia(entrega.entregueEm) : null;
  // A data da venda não comprova quando a entrega ocorreu.
  const entregueEm = diaLocal(entrega.entregaDetectadaEm);
  const dataEntregaConfirmada = entregaManualEm || (status === "ENTREGUE" ? entregueEm : null);
  let coluna;
  if (dataEntregaConfirmada) coluna = "entregue";
  else if (status === "ENTREGUE") coluna = "entregue";
  else if (status === "A ENTREGAR") coluna = tipo === "pronta_entrega" ? "pronta" : "deposito";
  else if (NA_FABRICA.has(status)) coluna = "fabrica";
  else if (tipo === "pronta_entrega") coluna = "pronta";
  else coluna = "outros";
  if (coluna === "entregue") {
    const mes = dia(hoje)?.slice(0, 7);
    // Sem data percebida (importação histórica), a venda limita a exposição
    // ao mês atual, mas NÃO passa a ser tratada como data de entrega.
    const dataReferencia = dataEntregaConfirmada || diaLocal(p.dataVenda);
    if (!mes || dataReferencia?.slice(0, 7) !== mes) return null;
  }
  const alertas = [];
  const faltam = previsao ? diasEntre(hoje, previsao) : null;
  if (coluna === "fabrica" || coluna === "outros") {
    if (faltam != null && faltam < 0) alertas.push({ nivel: "alto", texto: `Previsão do fornecedor passou há ${-faltam} dia${faltam === -1 ? "" : "s"}` });
    else if (faltam != null && faltam <= 2) alertas.push({ nivel: "medio", texto: faltam === 0 ? "Previsão é hoje: confira com o depósito e avise o cliente se atrasar" : `Previsão em ${faltam} dia${faltam === 1 ? "" : "s"}: confira com o depósito e avise o cliente se atrasar` });
  }
  if (status === "ATRASADO") alertas.push({ nivel: "alto", texto: "O LIFT marca este pedido como ATRASADO" });
  if (coluna === "pronta") {
    const d = dia(p.dataVenda) ? diasEntre(dia(p.dataVenda), hoje) : null;
    if (d != null && d > 7) alertas.push({ nivel: "alto", texto: `Pronta entrega vendida há ${d} dias` });
  }
  if (coluna === "deposito" && chegou) {
    const d = diasEntre(chegou, hoje);
    if (d != null && d > 3) alertas.push({ nivel: "medio", texto: `No depósito há ${d} dias: confirme se o cliente já foi agendado` });
  }
  return {
    coluna, tipo, status: status || "SEM STATUS", previsao, chegou,
    entregueEm, entregaManualEm, dataEntregaConfirmada, faltam, alertas,
  };
}

// A sessão fornece os dados permitidos; a tela também respeita a identidade
// em "Mudar visão", que é diferente da identidade autenticada do administrador.
export function pedidosDaVisao(pedidos, usuario) {
  if (!Array.isArray(pedidos) || !usuario) return [];
  if (["admin", "superadmin"].includes(usuario.role)) return pedidos;
  const loja = Number(usuario.storeId);
  if (!Number.isInteger(loja) || loja <= 0) return [];
  if (usuario.role === "manager") return pedidos.filter(p => Number(p?.storeId) === loja);
  if (usuario.role === "consultant") {
    const id = Number(usuario.id);
    if (!Number.isInteger(id) || id <= 0) return [];
    // Pedidos de outra loja vêm resumidos da API; não abrir ficha de outro cliente.
    return pedidos.filter(p => Number(p?.storeId) === loja
      && Number(p?.vendidoPor?.consultantId) === id);
  }
  return [];
}

// Monta as colunas: com alerta primeiro; depois pela previsão (mais cedo primeiro).
export function montarEntregas(pedidos, hoje) {
  const porColuna = Object.fromEntries(COLUNAS.map(c => [c.id, []]));
  for (const p of Array.isArray(pedidos) ? pedidos : []) {
    const info = classificarPedido(p, hoje);
    if (info) porColuna[info.coluna].push({ pedido: p, info });
  }
  const peso = x => (x.info.alertas.some(a => a.nivel === "alto") ? 0 : x.info.alertas.length ? 1 : 2);
  for (const lista of Object.values(porColuna)) {
    lista.sort((a, b) => peso(a) - peso(b) || String(a.info.previsao || "9999").localeCompare(String(b.info.previsao || "9999")));
  }
  return porColuna;
}
